/**
 * User row component for admin user table.
 */

import * as stylex from "@stylexjs/stylex";

import { badge, button, form, layout, table, text } from "@animus/primaris";

import type { AdminUser, Role } from "./types.ts";

interface UserRowProps {
  user: AdminUser;
  isAdmin: boolean;
  isCurrentUser: boolean;
  onBan: (reason?: string) => void;
  onUnban: () => void;
  onSetRole: (role: Role) => void;
  onDelete: () => void;
  onViewDetails: () => void;
}

function UserRow({
  user,
  isAdmin,
  isCurrentUser,
  onBan,
  onUnban,
  onSetRole,
  onDelete,
  onViewDetails,
}: UserRowProps) {
  return (
    <tr {...stylex.props(table.tr)}>
      <td {...stylex.props(table.td)}>
        <div>
          <p {...stylex.props(text.white)}>{user.name || "Unnamed"}</p>
          <p {...stylex.props(text.sm, text.muted)}>{user.email}</p>
        </div>
      </td>
      <td {...stylex.props(table.td)}>
        {isAdmin && !isCurrentUser ? (
          <select
            {...stylex.props(form.select)}
            onChange={(e) => onSetRole(e.target.value as Role)}
            value={user.role}
          >
            <option value="user">User</option>
            <option value="moderator">Moderator</option>
            <option value="admin">Admin</option>
          </select>
        ) : (
          <span {...stylex.props(badge.base, badge.neutral, text.capitalize)}>
            {user.role}
          </span>
        )}
      </td>
      <td {...stylex.props(table.td)}>
        {user.banned ? (
          <span {...stylex.props(badge.base, badge.red)}>Banned</span>
        ) : (
          <span {...stylex.props(badge.base, badge.green)}>Active</span>
        )}
      </td>
      <td {...stylex.props(table.td, text.sm, text.muted)}>
        {new Date(user.createdAt).toLocaleDateString()}
      </td>
      <td {...stylex.props(table.td, table.tdRight)}>
        <div {...stylex.props(layout.flexRow, layout.gap2)}>
          <button
            {...stylex.props(button.base, button.secondary, button.small)}
            onClick={onViewDetails}
            type="button"
          >
            View
          </button>
          {!isCurrentUser && (
            <>
              {user.banned ? (
                <button
                  {...stylex.props(button.base, button.success, button.small)}
                  onClick={onUnban}
                  type="button"
                >
                  Unban
                </button>
              ) : (
                <button
                  {...stylex.props(button.base, button.warning, button.small)}
                  onClick={() => onBan()}
                  type="button"
                >
                  Ban
                </button>
              )}
              {isAdmin && (
                <button
                  {...stylex.props(button.base, button.danger, button.small)}
                  onClick={onDelete}
                  type="button"
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export { UserRow, type UserRowProps };
